import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Users, BookOpen, Utensils, Home, HandHeart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ScriptureHeader from '../components/ScriptureHeader';

export default function AboutPage() {
  const { t } = useTranslation();

  const purposes = [
    { icon: BookOpen, title: t('about.purposes.religious', 'Religious Ministry'), desc: t('about.purposes.religiousDesc', 'Conducting Christian worship, preaching and teaching the Gospel of Jesus Christ') },
    { icon: Heart, title: t('about.purposes.evangelical', 'Evangelical Work'), desc: t('about.purposes.evangelicalDesc', 'Spreading the Gospel through revivals, prison ministries, and prayer hotlines') },
    { icon: Users, title: t('about.purposes.education', 'Christian Education'), desc: t('about.purposes.educationDesc', 'Establishing educational programs with biblically-based curriculum') },
    { icon: Utensils, title: t('about.purposes.charitable', 'Charitable Assistance'), desc: t('about.purposes.charitableDesc', 'Food pantries, disaster response, and humanitarian aid programs') },
    { icon: Home, title: t('about.purposes.social', 'Social Services'), desc: t('about.purposes.socialDesc', 'Shelters, counseling services, and family strengthening initiatives') },
    { icon: HandHeart, title: t('about.purposes.senior', 'Senior Care'), desc: t('about.purposes.seniorDesc', 'Providing affordable, high-quality care aligned with Christian principles') },
  ];

  const leadership = [
    { name: 'Liza Delgado', role: t('about.roles.president', 'President/Senior Pastor & Treasurer') },
    { name: 'Bernardo Madrigal', role: t('about.roles.coPastor', 'Co-Pastor') },
    { name: 'Isela Merino', role: 'Board Member / Animal Care Ministry Director' },
    { name: 'Julio Guerra', role: 'Board Member / Community Care Director - Polk County' },
    { name: 'Wendy Font', role: 'Board Member / Community Resource Director - Osceola & Orange County' },
  ];

  return (
    <div className="min-h-screen bg-cream pt-20" data-testid="about-page">
      {/* Scripture Header */}
      <ScriptureHeader page="about" />

      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-charcoal mb-4 block">{t('about.ourStory', 'About Us')}</span>
            <h1 className="font-serif text-4xl md:text-6xl text-charcoal mb-6">
              Living Fruit Ministry
            </h1>
            <p className="text-charcoal/70 font-sans text-lg max-w-3xl mx-auto leading-relaxed">
              {t('about.subtitle', 'A Florida 501(c)(3) nonprofit organization dedicated to serving God and humanity through religious ministry, evangelical outreach, education, and charitable assistance.')}
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Mission & Vision */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-burgundy rounded-3xl p-8 md:p-12 text-white"
            >
              <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">{t('about.ourMission', 'Our Mission')}</span>
              <h2 className="font-serif text-2xl md:text-3xl mb-4">{t('about.missionTitle', 'Bearing Fruit for God\'s Kingdom')}</h2>
              <p className="text-white/80 leading-relaxed">
                {t('about.missionText', 'To serve God and humanity through a comprehensive approach encompassing religious ministry, evangelical outreach, education, and charitable assistance, both domestically and internationally. We strive to be living examples of Christ\'s love, providing spiritual nourishment and practical support to all who seek it.')}
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-cream border border-gold/20 rounded-3xl p-8 md:p-12"
            >
              <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">{t('about.ourVision', 'Our Vision')}</span>
              <h2 className="font-serif text-2xl md:text-3xl text-charcoal mb-4">{t('about.visionTitle', 'A Community Transformed')}</h2>
              <p className="text-charcoal/70 leading-relaxed">
                {t('about.visionText', 'We envision a community where every individual experiences the transforming love of Christ, where no family goes hungry, and where spiritual growth leads to lasting positive change. Through our ministries, we aim to cultivate a garden of faith that bears abundant fruit for generations to come.')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Our Purposes */}
      <section className="py-16 md:py-24" data-testid="purposes-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">{t('home.whatWeDo', 'What We Do')}</span>
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal">{t('about.ourPurposes', 'Our Purposes')}</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {purposes.map((purpose, i) => (
              <motion.div
                key={purpose.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl border border-gold/10 hover:border-gold/30 transition-all duration-300 hover:-translate-y-1"
                data-testid={`purpose-${i}`}
              >
                <div className="bg-burgundy/10 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <purpose.icon size={24} className="text-burgundy" />
                </div>
                <h3 className="font-serif text-lg text-charcoal mb-2">{purpose.title}</h3>
                <p className="text-charcoal/60 text-sm">{purpose.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Leadership */}
      <section className="py-16 bg-white" data-testid="leadership-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">{t('about.ourTeam', 'Our Team')}</span>
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal">{t('about.leadership', 'Leadership')}</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {leadership.map((person, i) => (
              <motion.div
                key={person.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6"
                data-testid={`leader-${i}`}
              >
                <div className="w-20 h-20 bg-gradient-to-br from-burgundy to-purple rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-white font-serif text-2xl">{person.name.charAt(0)}</span>
                </div>
                <h3 className="font-serif text-lg text-charcoal">{person.name}</h3>
                <p className="text-charcoal/60 text-sm">{person.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Location */}
      <section className="py-16" data-testid="location-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="bg-charcoal rounded-3xl p-8 md:p-12 text-white text-center">
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">{t('about.visitUs', 'Visit Us')}</span>
            <h2 className="font-serif text-2xl md:text-3xl mb-4">{t('about.located', 'Located in Kissimmee, Florida')}</h2>
            <p className="text-white/70 mb-2">109 N Beaumont Avenue #1078</p>
            <p className="text-white/70 mb-6">Kissimmee, FL 34741</p>
            <div className="flex justify-center gap-6 text-sm">
              <div>
                <span className="text-gold">{t('contact.phone', 'Phone')}:</span>
                <span className="ml-2">(407) 216-2853</span>
              </div>
              <div>
                <span className="text-gold">{t('contact.email', 'Email')}:</span>
                <span className="ml-2">livingfruitministry@gmail.com</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
